export const ADMIN_DATE_FORMAT = 'YYYY-MM-DD';

export type AdminDateValue<T> = {
  startOf(unit: 'day'): T;
  endOf(unit: 'day'): T;
  millisecond(value: number): T;
  toISOString(): string;
};

export interface AdminQueryDateTimeRange {
  start_time: string;
  end_time: string;
}

/** 业务日期统一落在所选日期的 23:59:59，毫秒固定为 0。 */
export function adminEndOfDay<T extends AdminDateValue<T>>(value: T): T {
  return value.endOf('day').millisecond(0);
}

/** 查询日期范围的开始日期从当天 00:00:00 起算。 */
export function adminStartOfDay<T extends AdminDateValue<T>>(value: T): T {
  return value.startOf('day').millisecond(0);
}

export function toAdminEndOfDayISOString<T extends AdminDateValue<T>>(value: T): string {
  return adminEndOfDay(value).toISOString();
}

export function toAdminStartOfDayISOString<T extends AdminDateValue<T>>(value: T): string {
  return adminStartOfDay(value).toISOString();
}

/** 输出数据库本地无时区日期时间格式，避免 ISO UTC 转换造成日期偏移。 */
export function toAdminLocalDateTimeString(value: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
    + `T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
}

/** 按完整自然日构造查询范围：开始日 00:00:00，结束日 23:59:59。 */
export function adminQueryDateRange(
  days: number,
  referenceDate: Date = new Date(),
): AdminQueryDateTimeRange {
  const normalizedDays = Math.max(1, Math.floor(days));
  const start = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate() - (normalizedDays - 1),
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
    23,
    59,
    59,
    0,
  );
  return {
    start_time: toAdminLocalDateTimeString(start),
    end_time: toAdminLocalDateTimeString(end),
  };
}
